import React, { useEffect, useMemo, useState } from 'react';
import LandingShell from '@/components/landing/LandingShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Search,
  ExternalLink,
  BookOpen,
  Monitor,
  ShieldCheck,
  AppWindow,
  BrainCircuit,
  Workflow,
  TerminalSquare,
  Code2,
  Link2,
  Palette,
  Bot,
  Store,
  HelpCircle,
  ChevronLeft,
  Folder,
  LockKeyhole,
  Sparkles,
  Library,
  CheckCircle2,
  Settings2,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import SEO from '@/components/common/SEO';
import { makeOverrideIcon } from '@/OS/components/AppIcon';
import { PROGRAMS, PROGRAM_MAP, type MapleProgram } from '@/OS/utils/programs';

type AppDocEntry = {
  image: string;
  paragraphs: string[];
  features: string[];
};

type DocsTab = 'guides' | 'dev';

type DocsProps = {
  embedded?: boolean;
};

type SectionDescriptor = {
  id: string;
  label: string;
  icon: React.ReactNode;
};

const FALLBACK_APP_IMAGE = '/images/mapleos/MapleOS-Desktop-Thumb.png';
const DocsWelcomeIcon = makeOverrideIcon('help-menu', BookOpen);
const DocsDesktopIcon = makeOverrideIcon('desktop', Monitor);
const DocsPrivacyIcon = makeOverrideIcon('ai-access', ShieldCheck);
const DocsAppsIcon = makeOverrideIcon('all-programs', AppWindow);
const DocsKnowledgeIcon = makeOverrideIcon('knowledge', BrainCircuit);
const DocsBlueprintsIcon = makeOverrideIcon('ai-blueprints', Workflow);
const DocsPowerIcon = makeOverrideIcon('terminal', TerminalSquare);
const DocsArchitectureIcon = makeOverrideIcon('maple-engine', Code2);
const DocsAppModelIcon = makeOverrideIcon('file-explorer', Link2);
const DocsDesignIcon = makeOverrideIcon('display-properties', Palette);
const DocsAutomationIcon = makeOverrideIcon('blueprint-studio', Bot);
const DocsMarketIcon = makeOverrideIcon('maple-market', Store);
const DocsFaqIcon = makeOverrideIcon('help-menu', HelpCircle);
const DocsFolderIcon = makeOverrideIcon('folder', Folder);
const WelcomeDesktopCardIcon = makeOverrideIcon('desktop', Monitor);
const WelcomeControlCardIcon = makeOverrideIcon('ai-access', ShieldCheck);
const WelcomeAiCardIcon = makeOverrideIcon('ai-center', Sparkles);

const userSections: SectionDescriptor[] = [
  { id: 'welcome', label: 'Welcome & Philosophy', icon: <DocsWelcomeIcon className="h-5 w-5" /> },
  { id: 'how-it-works', label: 'How MapleOS Works', icon: <DocsDesktopIcon className="h-5 w-5" /> },
  { id: 'privacy-control', label: 'Privacy & Control', icon: <DocsPrivacyIcon className="h-5 w-5" /> },
  { id: 'apps', label: 'Apps', icon: <DocsAppsIcon className="h-5 w-5" /> },
  { id: 'knowledge-memory', label: 'Knowledge & Memory', icon: <DocsKnowledgeIcon className="h-5 w-5" /> },
  { id: 'blueprints', label: 'Blueprints', icon: <DocsBlueprintsIcon className="h-5 w-5" /> },
  { id: 'power-users', label: 'Power Users', icon: <DocsPowerIcon className="h-5 w-5" /> },
];

const devSections: SectionDescriptor[] = [
  { id: 'architecture', label: 'Architecture Overview', icon: <DocsArchitectureIcon className="h-5 w-5" /> },
  { id: 'app-model', label: 'App Model & Deep Links', icon: <DocsAppModelIcon className="h-5 w-5" /> },
  { id: 'ui-guidelines', label: 'UI/Design Guidelines', icon: <DocsDesignIcon className="h-5 w-5" /> },
  { id: 'automation', label: 'Automation & Blueprints', icon: <DocsAutomationIcon className="h-5 w-5" /> },
  { id: 'packaging', label: 'Packaging & Maple Market', icon: <DocsMarketIcon className="h-5 w-5" /> },
  { id: 'faq', label: 'Roadmap & FAQ', icon: <DocsFaqIcon className="h-5 w-5" /> },
];

const defaultSectionByTab: Record<DocsTab, string> = {
  guides: 'welcome',
  dev: 'architecture',
};

const APP_DOCS: Record<string, AppDocEntry> = {
  'ai-chat': {
    image: '/images/mapleos/MapleOS-AICenter.png',
    paragraphs: [
      'AI Chat is the conversational layer inside MapleOS, built to feel like part of the desktop instead of a detached prompt box. Open it beside your files, documents, and tools so thinking, drafting, and acting happen in one workspace.',
      'MapleOS keeps this experience human-first. You can use AI Chat with optional Trust Mode, selective access to files and apps, and controls that let you decide how much context the assistant should receive before it acts.',
      'When you launch AI Chat from a deep link or the start menu, it opens right inside the MapleOS shell, ready to work with the rest of your environment rather than pull you out of it.'
    ],
    features: [
      'Conversational workspace integrated into the MapleOS desktop',
      'Works with optional Trust Mode and permission-aware access controls',
      'Useful alongside files, projects, and system tools in separate windows',
      'Launches directly with the MapleOS deep link flow'
    ]
  },
  'ai-knowledge': {
    image: FALLBACK_APP_IMAGE,
    paragraphs: [
      'AI Knowledge is the place where private libraries become usable context inside MapleOS. It helps you organize documents, notes, and reference material so MaplePT can answer with grounding instead of guesswork.',
      'Unlike memory, knowledge is library-shaped. You choose the source material, decide when it should be consulted, and can keep those collections separate for different workflows, teams, or topics.',
      'Because it lives inside MapleOS, AI Knowledge pairs naturally with File Explorer, AI Chat, and projects. You can move from collecting material to asking better questions without leaving the desktop.'
    ],
    features: [
      'Organizes private libraries for grounded responses',
      'Separate from memory so context remains intentional and reviewable',
      'Works naturally with File Explorer, AI Chat, and projects',
      'Can be opened directly in MapleOS from Docs'
    ]
  },
  'ai-center': {
    image: '/images/mapleos/MapleOS-AICenter.png',
    paragraphs: [
      'AI Center gives MapleOS users a clear starting point for intelligent work. It brings together MaplePT-powered tools, active workflows, and app entry points so the AI layer feels organized instead of scattered.',
      'This is where the platform philosophy becomes visible: AI is useful, but it stays inside a system shaped around human control. From here, users can move into chat, projects, blueprints, and knowledge with a better sense of what each tool is for.',
      'As part of the operating system, AI Center works best when you keep it open alongside the rest of your desktop. It is a launch surface, an orientation layer, and a practical overview of MapleOS intelligence in context.'
    ],
    features: [
      'Central hub for MapleOS intelligence features',
      'Highlights MaplePT, blueprints, projects, and guided workflows',
      'Helps users navigate the AI layer without losing desktop context',
      'Available through the standard MapleOS deep link pattern'
    ]
  },
  'ai-blueprints': {
    image: '/images/blueprints.png',
    paragraphs: [
      'AI Blueprints is the fast-launch surface for repeatable workflows in MapleOS. It is built for people who already know the flow they want and simply need a clean way to run it again.',
      'In everyday use, this app sits between intent and execution. It lets teams and individuals revisit trusted patterns without reopening the full authoring environment every time.',
      'Because it runs as a MapleOS app, blueprints stay close to your files, access controls, and review surfaces. That makes repeatability practical without giving up visibility.'
    ],
    features: [
      'Quick access to saved automation patterns',
      'Built for repeatable workflows, not one-off experiments',
      'Keeps runs close to MapleOS permissions and review flows',
      'Deep-links directly into blueprint-driven work'
    ]
  },
  'ai-projects': {
    image: '/images/mapleos/hero-slider/ai-projects.png',
    paragraphs: [
      'AI Projects gives MapleOS a durable place for multi-session work. Instead of letting prompts disappear into isolated threads, it keeps files, outputs, and context together as a project that can evolve over time.',
      'This matters most when work needs continuity. Research, product planning, writing, and analysis all benefit when the desktop remembers the structure of the work even if the memory layer stays sealed or off.',
      'As an OS-native project surface, AI Projects connects naturally to the apps around it. Open a file, compare windows, return to a chat, and keep building without context-switching out of MapleOS.'
    ],
    features: [
      'Project workspace for durable AI-assisted work',
      'Keeps files, outputs, and context connected in one place',
      'Supports continuity without forcing background memory',
      'Deep-linkable for direct project entry'
    ]
  },
  'maple-access': {
    image: FALLBACK_APP_IMAGE,
    paragraphs: [
      'AI Chat Access is where MapleOS makes its control philosophy practical. It helps you define what the assistant can see, what it can touch, and what always needs a human decision first.',
      'This is especially useful for users who want the benefits of automation without surrendering visibility. Permissions can stay narrow, temporary, or task-specific depending on how cautious or open you want the system to be.',
      'In the wider MapleOS workflow, AI Chat Access works like a guardrail layer. It gives users confidence that helpful automation can remain explicit, permissioned, and reversible.'
    ],
    features: [
      'Control layer for AI visibility and actions',
      'Supports narrow, explicit, and reviewable permissions',
      'Pairs naturally with Trust Mode and Settings',
      'Accessible from Docs through MapleOS deep links'
    ]
  },
  'file-explorer': {
    image: '/images/mapleos/MapleOS-Files.png',
    paragraphs: [
      'File Explorer is the connective tissue of MapleOS. It gives users a familiar window into files, folders, and project materials while keeping everything anchored inside the desktop shell.',
      'Because MapleOS is built around user control, File Explorer is also where local-first habits become concrete. You can inspect what exists, choose what to share with AI tools, and keep sensitive material separated unless you explicitly grant access.',
      'Nearly every workflow touches File Explorer eventually. It is how apps stay connected, how projects remain understandable, and how the operating system keeps real work visible.'
    ],
    features: [
      'Browse files and folders from inside the MapleOS desktop',
      'Supports local-first organization and permission-aware workflows',
      'Connects naturally with knowledge, projects, and office tools',
      'Opens directly through the MapleOS deep link format'
    ]
  },
  terminal: {
    image: '/images/mapleos/MapleOS-Terminal.png',
    paragraphs: [
      'Terminal brings direct execution into MapleOS for users who prefer command lines, scripts, and inspection over point-and-click flows. It is a core part of the power-user story, not an afterthought.',
      'The benefit inside MapleOS is proximity. You can run commands while keeping File Explorer, code, and system surfaces visible in nearby windows, which makes technical work feel more integrated and less fragmented.',
      'For privacy-minded workflows, Terminal also reinforces a local-first mindset. It keeps advanced control close to the user, where configuration, diagnostics, and repeatable routines can remain transparent.'
    ],
    features: [
      'Command-line control inside the MapleOS desktop',
      'Ideal for scripts, diagnostics, and direct environment work',
      'Fits alongside files, code, and system panels in-window',
      'Launches instantly with the existing deep link pattern'
    ]
  },
  'blueprint-studio': {
    image: '/images/mapleos/apps/blueprint-studio.png',
    paragraphs: [
      'Blueprint Studio is the visual authoring surface for multi-step workflows in MapleOS. It is where repeatable logic becomes visible, editable, and understandable by humans before it is automated.',
      'That visibility matters. MapleOS treats automation as something you should be able to inspect, review, and improve rather than a black box hidden behind a button.',
      'Within the operating system, Blueprint Studio pairs naturally with AI Blueprints, projects, and access controls. You can design a flow, decide what it may touch, and run it from a desktop-native environment.'
    ],
    features: [
      'Visual builder for human-readable automation flows',
      'Encourages review, iteration, and safe reuse',
      'Works with MapleOS access controls and project workflows',
      'Pairs with AI Blueprints for fast relaunching'
    ]
  },
  'office-suite': {
    image: '/images/mapleos/apps/office-suite.png',
    paragraphs: [
      'Office Suite is the productivity layer inside MapleOS for writing, spreadsheets, and everyday deliverables. It gives users a familiar starting point while still benefiting from the broader desktop environment around it.',
      'What makes it feel different inside MapleOS is proximity to the rest of the system. Files, chat, knowledge, and references can all live nearby in separate windows instead of being scattered across disconnected tabs.',
      'For teams and individuals who want local-first habits with optional AI assistance, Office Suite is where practical work gets finished without leaving the platform.'
    ],
    features: [
      'Productivity hub for documents, tables, and calculations',
      'Keeps everyday work close to files, knowledge, and chat',
      'Supports local-first workflows with optional AI help',
      'Deep-links directly into MapleOS'
    ]
  },
  'maple-word': {
    image: '/images/mapleos/apps/maple-word.png',
    paragraphs: [
      'MapleWord is the writing surface in MapleOS for drafting, refining, and finishing documents without leaving the desktop. It feels familiar enough to start quickly and integrated enough to stay in flow.',
      'Because it sits inside MapleOS, MapleWord works well next to research, files, and AI-assisted brainstorming. You can keep source material visible, draft in one window, and stay grounded in your own context.',
      'For privacy-conscious work, that matters: the document remains part of a user-controlled workspace where assistance is optional and access can stay explicit.'
    ],
    features: [
      'Focused document editor inside MapleOS',
      'Easy to use beside research, files, and chat windows',
      'Supports user-controlled assistance instead of forced automation',
      'Openable from Docs with the native deep link flow'
    ]
  },
  'maple-sheet': {
    image: '/images/mapleos/apps/maple-sheet.png',
    paragraphs: [
      'MapleSheet is the spreadsheet workspace in MapleOS for planning, tracking, and lightweight analysis. It keeps structured thinking close to the rest of your operating environment.',
      'Inside MapleOS, spreadsheets do not have to live alone. You can compare source material in adjacent windows, bring in grounded context from knowledge, and keep calculations near the project they support.',
      'That makes MapleSheet a strong fit for users who want practical analysis with local-first habits and optional assistance rather than opaque automation.'
    ],
    features: [
      'Spreadsheet workspace for tables, formulas, and planning',
      'Useful beside files, knowledge, and project windows',
      'Fits local-first workflows with optional AI support',
      'Launches directly through MapleOS deep links'
    ]
  },
  'maple-calc': {
    image: '/images/mapleos/apps/maple-calc.png',
    paragraphs: [
      'MapleCalc is the fast utility calculator for MapleOS, ready for everyday arithmetic and quick checks while larger work stays open around it.',
      'Its value comes from immediacy. Open it, answer the question, and return to the task without breaking flow or leaving the desktop.',
      'Small utilities matter in an operating system. MapleCalc helps MapleOS feel complete by making even simple math part of the same cohesive workspace.'
    ],
    features: [
      'Fast calculator for everyday problem solving',
      'Designed for quick use during larger desktop workflows',
      'Keeps lightweight utility work inside MapleOS',
      'Available via the standard deep link button'
    ]
  },
  'control-panel': {
    image: FALLBACK_APP_IMAGE,
    paragraphs: [
      'Control Panel is the home for core MapleOS settings, where users tune the behavior of the desktop, review preferences, and decide how much the system should automate on their behalf.',
      'This app is central to the privacy-and-control story. It is where system defaults become personal choices, whether that means keeping things local-first, adjusting access boundaries, or refining the overall feel of the desktop.',
      'Within MapleOS, Control Panel acts like the configuration backbone. It helps the rest of the apps stay coherent because users have one trusted place to shape how the operating system works.'
    ],
    features: [
      'Central settings surface for MapleOS behavior and preferences',
      'Helps users tune privacy, access, and desktop defaults',
      'Supports a more explicit, user-controlled operating model',
      'Openable directly inside MapleOS from Docs'
    ]
  },
  'app-store': {
    image: FALLBACK_APP_IMAGE,
    paragraphs: [
      'Maple Market is the discovery and installation surface for apps in MapleOS. It gives the operating system a clean path from exploring something new to opening it as part of your existing desktop workflow.',
      'The emphasis is practical rather than noisy. You discover apps, understand what they are for, install them into the system, and launch them with the same consistent windowing and deep-link behavior as everything else.',
      'For developers, Maple Market is also the packaging destination. For users, it is simply the most direct way to grow the OS around the work they actually do.'
    ],
    features: [
      'App discovery and install flow for MapleOS',
      'Connects browse, install, and open into one lifecycle',
      'Keeps new apps inside the same desktop conventions',
      'Accessible through the Docs deep link button'
    ]
  },
  mapleclaw: {
    image: FALLBACK_APP_IMAGE,
    paragraphs: [
      'MapleClaw is a direct control surface for MapleOS actions and more operational workflows. It is designed for users who want a tighter loop between intent, review, and execution.',
      'Inside MapleOS, MapleClaw makes automation feel more deliberate. It is less about passive assistance and more about giving the user explicit leverage over what happens next.',
      'That makes it especially useful for advanced workflows where transparency matters as much as speed.'
    ],
    features: [
      'Operational control surface for MapleOS actions',
      'Useful for deliberate, reviewable workflow execution',
      'Built for users who want more direct system leverage',
      'Launchable from Docs with the standard deep link'
    ]
  },
  cortex: {
    image: FALLBACK_APP_IMAGE,
    paragraphs: [
      'Cortex shows that MapleOS is a real desktop environment, not only a productivity shell. It brings a fast-paced game experience into the same operating context as work, utilities, and creative tools.',
      'That may sound small, but it says something important about the platform: the app model is broad enough to support focused work and richer interactive surfaces without changing how the OS behaves.',
      'As with any MapleOS program, Cortex can be launched, reopened, and placed inside the wider desktop flow through the same familiar conventions.'
    ],
    features: [
      'Game experience that still behaves like a MapleOS app',
      'Demonstrates the flexibility of the MapleOS app model',
      'Lives within the same launch and windowing system as other tools',
      'Can be opened through the existing deep link behavior'
    ]
  },
};

const getAppDocContent = (program: MapleProgram): AppDocEntry => {
  const appDoc = APP_DOCS[program.id];

  if (appDoc) {
    return appDoc;
  }

  const folderName = program.folder ?? 'Core';

  return {
    image: FALLBACK_APP_IMAGE,
    paragraphs: [
      program.description ?? `${program.label} is part of the MapleOS app ecosystem.`,
      `${program.label} lives in the ${folderName} area of MapleOS and is designed to work as part of a connected desktop workflow. You can open it from the start menu, keep it beside related apps, and move through tasks without leaving the operating system.`,
      `From Docs, you can jump straight into ${program.label} with the built-in deep link. That makes it easy to explore how the app fits into MapleOS while keeping privacy, permissions, and local-first habits in view.`
    ],
    features: [
      `Located in the MapleOS ${folderName} category`,
      'Works inside the MapleOS desktop and windowing model',
      'Fits permission-aware and local-first workflows where relevant',
      'Can be opened directly with the MapleOS deep link button'
    ]
  };
};

const WelcomePhilosophyContent = () => (
  <div className="space-y-8">
    <div className="space-y-4">
      <Badge variant="secondary" className="w-fit">Documentation</Badge>
      <div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Welcome to MapleOS</h1>
        <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground sm:text-lg">
          MapleOS is a desktop operating experience built around apps, privacy, and human control.
          It works beautifully as a classic desktop, and it becomes even more powerful when you add optional AI,
          repeatable blueprints, and permission-aware automation.
        </p>
      </div>
    </div>

    <div className="grid gap-4 md:grid-cols-3">
      {[
        {
          title: 'Desktop first',
          description: 'Start from the desktop, keep multiple windows open, and move naturally between files, tools, and focused work.',
          icon: <WelcomeDesktopCardIcon className="h-6 w-6" />,
        },
        {
          title: 'Human in control',
          description: 'Trust Mode is optional, Sealed Memory is opt-in, and access controls define what MapleOS can see or do.',
          icon: <WelcomeControlCardIcon className="h-6 w-6" />,
        },
        {
          title: 'Useful with or without AI',
          description: 'Office tools, files, utilities, and apps stand on their own. AI is there to help, not to take over.',
          icon: <WelcomeAiCardIcon className="h-6 w-6" />,
        },
      ].map((item) => (
        <Card key={item.title} className="border-border/70">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              {item.icon}
              <CardTitle className="text-lg text-zinc-200">{item.title}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>

    <Card className="border-border/70 bg-muted/30">
      <CardHeader>
        <CardTitle className="text-xl">The MapleOS philosophy</CardTitle>
        <CardDescription>Software should give humans back leverage, not take it away.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-3 text-sm leading-7 text-muted-foreground sm:text-base">
          <p>
            MapleOS treats the desktop as a place for real work: documents, files, windows, utilities, and apps that fit together.
            Intelligence belongs inside that system, not above it.
          </p>
          <p>
            That is why MapleOS emphasizes visible controls. You can keep interactions sandboxed, seal memory until you explicitly opt in,
            and decide when the system should act with more autonomy.
          </p>
        </div>
        <div className="rounded-2xl border bg-background p-5">
          <h3 className="mb-4 text-lg font-semibold">What that means in practice</h3>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" /> Apps stay understandable and easy to open from the desktop.</li>
            <li className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" /> MaplePT can assist, but the desktop remains useful even when AI is off.</li>
            <li className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" /> Users choose when knowledge, memory, and automation become part of a workflow.</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  </div>
);

const HowWorksContent = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-3xl font-bold tracking-tight">How MapleOS works</h2>
      <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
        MapleOS behaves like a desktop OS with a start surface, windows, apps, files, and deep links.
        The goal is simple: let people move through real workflows without hopping between disconnected products.
      </p>
    </div>

    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {[
        {
          title: 'Start menu and launcher',
          description: 'Browse programs by category, search quickly, and open what you need without losing orientation.',
        },
        {
          title: 'Windowed apps',
          description: 'Run multiple apps side by side so chat, files, notes, and tools can support one another in view.',
        },
        {
          title: 'VFS and files',
          description: 'Use File Explorer as the shared layer for project materials, local-first work, and explicit file access.',
        },
        {
          title: 'Deep links',
          description: 'Open programs directly with stable links like /desktop/start?open=id when you want fast entry into a task.',
        },
      ].map((item) => (
        <Card key={item.title} className="border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{item.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>

    <Card className="border-border/70">
      <CardHeader>
        <CardTitle className="text-xl">A typical MapleOS workflow</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-4 pl-5 text-sm text-muted-foreground sm:text-base">
          <li className="list-decimal"><span className="font-medium text-foreground">Open from the desktop.</span> Launch an app from the start menu or jump in with a deep link.</li>
          <li className="list-decimal"><span className="font-medium text-foreground">Keep context visible.</span> Place File Explorer, Office tools, AI Chat, or projects in separate windows.</li>
          <li className="list-decimal"><span className="font-medium text-foreground">Grant only what is needed.</span> If AI needs context, you can choose knowledge libraries, memory, or access controls deliberately.</li>
          <li className="list-decimal"><span className="font-medium text-foreground">Turn patterns into workflows.</span> When a task becomes repeatable, capture it with Blueprints and run it again safely.</li>
        </ol>
      </CardContent>
    </Card>
  </div>
);

const PrivacyControlContent = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-3xl font-bold tracking-tight">Privacy & Control</h2>
      <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
        MapleOS is designed so helpful automation does not have to mean hidden automation.
        You decide how much context the system gets, how long it keeps it, and when it is allowed to act.
      </p>
    </div>

    <div className="grid gap-4 lg:grid-cols-3">
      {[
        {
          title: 'Trust Mode',
          description: 'Optional. Use it when you want a smoother, more capable assistant experience; leave it off when you want tighter boundaries and more review.',
          icon: <ShieldCheck className="h-5 w-5" />,
        },
        {
          title: 'Sealed Memory',
          description: 'Opt-in. Memory can stay sealed by default so conversations remain session-bound until you intentionally preserve what matters.',
          icon: <LockKeyhole className="h-5 w-5" />,
        },
        {
          title: 'Access Controls',
          description: 'Explicit permissions define what MapleOS may see, read, or act on across files, apps, and workflows.',
          icon: <Settings2 className="h-5 w-5" />,
        },
      ].map((item) => (
        <Card key={item.title} className="border-border/70">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-primary">
              {item.icon}
              <CardTitle className="text-lg">{item.title}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>

    <Alert>
      <ShieldCheck className="h-4 w-4" />
      <AlertTitle>Where to manage these settings</AlertTitle>
      <AlertDescription>
        Look in Chat controls for session-level behavior and in Settings or Control Panel for broader defaults.
        MapleOS keeps these choices close to the user so privacy is part of the workflow, not buried behind it.
      </AlertDescription>
    </Alert>

    <Card className="border-border/70 bg-muted/30">
      <CardHeader>
        <CardTitle className="text-xl">A practical way to think about it</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-3 text-sm leading-7 text-muted-foreground sm:text-base">
          <p>
            Knowledge and memory are not the same thing. Knowledge is a library you intentionally provide for grounding.
            Memory is a longer-lived layer that should remain your choice.
          </p>
          <p>
            Access controls add another layer: even when MapleOS can help, that does not mean it should automatically inspect every file,
            trigger every action, or move through the whole desktop unchecked.
          </p>
        </div>
        <div className="rounded-2xl border bg-background p-5">
          <h3 className="mb-4 text-lg font-semibold">Good defaults</h3>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" /> Start narrow and expand only when a workflow clearly benefits.</li>
            <li className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" /> Prefer visible review steps for important actions and edits.</li>
            <li className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" /> Keep sensitive work local-first when that matches your environment and risk tolerance.</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  </div>
);

const AppsContent = () => {
  const folderCounts = PROGRAMS.reduce<Record<string, number>>((acc, program) => {
    const key = program.folder ?? 'Core';
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Desktop & All Apps</h2>
        <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
          MapleOS is built around an app ecosystem that feels coherent on the desktop.
          From office tools to system utilities and AI workspaces, each app opens in the same operating context and can be launched from Docs with a deep link.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">One launch model</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">
              Every program follows the same desktop entry pattern, which keeps the platform easy to learn and faster to navigate.
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Designed to work together</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">
              File Explorer, Office tools, projects, knowledge, and system apps all fit together as one environment instead of isolated products.
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Open in MapleOS</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">
              Use the app list in the sidebar to open program-specific docs and jump directly into the live OS with the existing deep link flow.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/70 bg-muted/30">
        <CardHeader>
          <CardTitle className="text-xl">App families in MapleOS</CardTitle>
          <CardDescription>Programs are grouped to keep the system understandable as it grows.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Object.entries(folderCounts)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([folder, count]) => (
              <div key={folder} className="rounded-2xl border bg-background p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold">{folder}</h3>
                  <Badge variant="outline">{count} apps</Badge>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Explore the {folder.toLowerCase()} apps from the sidebar to see how each program fits into MapleOS.
                </p>
              </div>
            ))}
        </CardContent>
      </Card>
    </div>
  );
};

const KnowledgeMemoryContent = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-3xl font-bold tracking-tight">Knowledge & Memory</h2>
      <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
        MapleOS separates grounded knowledge from longer-lived memory so users can keep context useful without making it invisible.
      </p>
    </div>

    <div className="grid gap-4 md:grid-cols-2">
      <Card className="border-border/70">
        <CardHeader>
          <div className="flex items-center gap-2 text-primary">
            <Library className="h-5 w-5" />
            <CardTitle className="text-lg">Knowledge</CardTitle>
          </div>
          <CardDescription>Your private libraries for grounded answers.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li>Built from documents, notes, and materials you intentionally provide.</li>
            <li>Useful when you want answers tied to your own sources.</li>
            <li>Often paired with citations or source-aware responses so you can inspect where the answer came from.</li>
          </ul>
        </CardContent>
      </Card>
      <Card className="border-border/70">
        <CardHeader>
          <div className="flex items-center gap-2 text-primary">
            <LockKeyhole className="h-5 w-5" />
            <CardTitle className="text-lg">Sealed Memory</CardTitle>
          </div>
          <CardDescription>Longer-lived context that stays opt-in.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li>Best for durable preferences or recurring context you want MapleOS to remember.</li>
            <li>Should remain under your control, especially for sensitive or evolving work.</li>
            <li>Useful when continuity matters, but not required for MapleOS to be effective.</li>
          </ul>
        </CardContent>
      </Card>
    </div>

    <Card className="border-border/70 bg-muted/30">
      <CardHeader>
        <CardTitle className="text-xl">Plain-language guidance</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm leading-7 text-muted-foreground sm:text-base">
        <p>
          If you want the assistant to answer from your documents, use knowledge. If you want the system to remember patterns over time,
          consider memory. They solve different problems.
        </p>
        <p>
          When citations or source references are available, turn them on for higher-trust workflows. They help you verify what grounded the response,
          which is especially helpful for research, policy, and team handoffs.
        </p>
      </CardContent>
    </Card>
  </div>
);

const BlueprintsContent = () => {
  const [open, setOpen] = useState(false);

  const nodes = [
    { title: 'Start', description: 'Every blueprint begins with an explicit entry point.', image: '/images/bp/start.png' },
    { title: 'End', description: 'Close the flow clearly so outputs and actions stay reviewable.', image: '/images/bp/end.png' },
    { title: 'System Prompt', description: 'Set the operating instructions for how the workflow should behave.', image: '/images/bp/system.png' },
    { title: 'User Input', description: 'Collect the user input that should guide the rest of the run.', image: '/images/bp/user.png' },
    { title: 'Model Selector', description: 'Choose the MaplePT runtime or model behavior for the task at hand.', image: '/images/bp/model.png' },
    { title: 'AI Response', description: 'Produce the generated output that the user will review or reuse.', image: '/images/bp/airesponse.png' },
    { title: 'API Call', description: 'Connect to an external system when a workflow genuinely needs live data or actions.', image: '/images/bp/apicall.png' },
    { title: 'Action', description: 'Trigger a visible step in your workflow or app flow.', image: '/images/bp/action.png' },
    { title: 'Knowledge Base', description: 'Ground the workflow in selected documents and private reference material.', image: '/images/bp/knowledgebase.png' },
    { title: 'Data', description: 'Provide constants, defaults, or structured values that keep runs predictable.', image: '/images/bp/data.png' },
    { title: 'Configuration', description: 'Adjust runtime behavior and execution settings for the flow.', image: '/images/bp/configuration.png' },
    { title: 'MCP', description: 'Invoke machine-callable services for structured automation when that level of power is appropriate.', image: '/images/bp/mcp.png' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Blueprints</h2>
        <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
          Blueprints let you turn a good process into a repeatable MapleOS workflow.
          The emphasis is not just speed, but visibility: people should be able to inspect a flow, understand what it does,
          and decide how much autonomy it deserves.
        </p>
      </div>

      <div className="space-y-4">
        <img
          src="/images/blueprints.png"
          alt="Blueprints overview"
          className="cursor-pointer rounded-lg shadow-md transition hover:opacity-90"
          onClick={() => setOpen(true)}
        />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-screen-2xl">
            <img src="/images/blueprints.png" alt="Expanded blueprints overview" className="h-auto w-full rounded-lg" />
          </DialogContent>
        </Dialog>
        <p className="text-sm leading-7 text-muted-foreground sm:text-base">
          The blueprint shown above demonstrates a structured flow for analyzing data with human-readable steps,
          grounded context, and a clear execution path from input to outcome.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {[
          {
            title: 'Safe by design',
            description: 'Prefer explicit inputs, clear outputs, and visible actions. Important workflows should be easy to review before and after a run.',
          },
          {
            title: 'Grounded when needed',
            description: 'Bring in knowledge libraries or structured data when accuracy matters more than raw creativity.',
          },
          {
            title: 'Human checkpoints',
            description: 'Keep approval steps in the loop for actions that affect files, messaging, or shared work.',
          },
        ].map((item) => (
          <Card key={item.title} className="border-border/70">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{item.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div>
        <h3 className="mb-4 text-xl font-bold">Available node types</h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {nodes.map((node) => (
            <Card key={node.title} className="border bg-card transition hover:shadow-lg">
              <CardHeader className="flex flex-col items-center pb-2">
                <img src={node.image} alt={node.title} className="h-20 w-64 rounded-xl object-contain shadow-md" />
                <CardTitle className="text-center text-lg">{node.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-center text-sm text-muted-foreground">{node.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Card className="border-border/70 bg-muted/30">
        <CardHeader>
          <CardTitle className="text-xl">Good blueprint habits</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm text-muted-foreground sm:text-base">
            <li><span className="font-medium text-foreground">Start simple.</span> Build the shortest flow that proves the pattern before you add branches and extra actions.</li>
            <li><span className="font-medium text-foreground">Keep secrets out of public flows.</span> Sensitive credentials and high-trust actions should stay in protected configuration, not inside a shared blueprint.</li>
            <li><span className="font-medium text-foreground">Name nodes clearly.</span> Future-you and collaborators should be able to read the workflow like a process map.</li>
            <li><span className="font-medium text-foreground">Use review steps for important actions.</span> Transparent automation is usually safer and easier to improve.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

const PowerUsersContent = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-3xl font-bold tracking-tight">Power Users</h2>
      <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
        MapleOS is friendly to people who like direct control. If you prefer terminals, configuration, repeatable routines,
        and local-first habits, the system is designed to meet you there.
      </p>
    </div>

    <div className="grid gap-4 md:grid-cols-3">
      {[
        {
          title: 'Terminal workflows',
          description: 'Keep command-line work inside the same desktop context as files, code, and system tools.',
        },
        {
          title: 'Local-first patterns',
          description: 'Use apps and data flows that keep work close to the user whenever that is the right fit.',
        },
        {
          title: 'User-controlled configuration',
          description: 'Adjust how much the system remembers, sees, or automates so the desktop matches your operating style.',
        },
      ].map((item) => (
        <Card key={item.title} className="border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{item.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>

    <Card className="border-border/70 bg-muted/30">
      <CardHeader>
        <CardTitle className="text-xl">Recommended approach</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm leading-7 text-muted-foreground sm:text-base">
        <p>
          Start with narrow permissions, visible review steps, and the simplest app stack that solves the task.
          Once a workflow proves itself, capture it in a blueprint or a project structure so it stays reusable.
        </p>
        <p>
          This keeps MapleOS fast, understandable, and resilient. Power comes from good structure and explicit control,
          not from turning on every capability at once.
        </p>
      </CardContent>
    </Card>
  </div>
);

const ArchitectureOverviewContent = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-3xl font-bold tracking-tight">Architecture Overview</h2>
      <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
        MapleOS is organized like a desktop shell with app surfaces, windowing, shared file access, and stable deep links.
        Developer documentation here stays intentionally high-level: enough to build in the MapleOS style without overloading the page.
      </p>
    </div>

    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {[
        { title: 'Desktop shell', description: 'The outer operating context for launching, arranging, and returning to programs.' },
        { title: 'Program surfaces', description: 'Apps are identified by stable IDs and opened inside consistent MapleOS chrome.' },
        { title: 'Windowing model', description: 'Users should be able to compare and combine apps side by side during real workflows.' },
        { title: 'Deep links', description: 'Links should move a user into the right program quickly without inventing a separate navigation system.' },
      ].map((item) => (
        <Card key={item.title} className="border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{item.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>

    <Card className="border-border/70 bg-muted/30">
      <CardHeader>
        <CardTitle className="text-xl">Principle for developers</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm leading-7 text-muted-foreground sm:text-base">
        <p>
          A MapleOS app should feel like it belongs to the desktop before it tries to be clever.
          Users should understand where it lives, how to open it, and how it relates to the rest of the system.
        </p>
        <p>
          If your app uses automation or AI, design those capabilities as visible layers inside the app model rather than hidden replacements for it.
        </p>
      </CardContent>
    </Card>
  </div>
);

const AppModelContent = () => {
  const samplePrograms = PROGRAMS.slice(0, 8);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">App Model & Deep Links</h2>
        <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
          MapleOS programs are defined by stable IDs and launched through the desktop shell.
          The standard deep link pattern stays simple: <code className="rounded bg-muted px-1.5 py-0.5 text-sm">/desktop/start?open=id</code>.
        </p>
      </div>

      <Card className="border-border/70">
        <CardHeader>
          <CardTitle className="text-xl">What to rely on</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm text-muted-foreground sm:text-base">
            <li>Use the program ID as the durable reference for linking and launching.</li>
            <li>Keep labels clear and user-facing; keep IDs predictable and stable.</li>
            <li>Make sure a deep-linked app still feels like it opened inside MapleOS, not outside it.</li>
          </ul>
        </CardContent>
      </Card>

      <div>
        <h3 className="mb-4 text-xl font-bold">Sample program IDs</h3>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {samplePrograms.map((program) => (
            <Card key={program.id} className="border-border/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{program.label}</CardTitle>
                <CardDescription>{program.id}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">Open with <code className="rounded bg-muted px-1.5 py-0.5 text-xs">/desktop/start?open={program.id}</code></p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

const UIGuidelinesContent = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-3xl font-bold tracking-tight">UI/Design Guidelines</h2>
      <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
        Design for clarity, consistency, and desktop flow. MapleOS apps should feel native to the operating environment,
        even when the content inside them is unique.
      </p>
    </div>

    <div className="grid gap-4 md:grid-cols-2">
      {[
        {
          title: 'Respect window chrome',
          description: 'Assume the app lives inside a broader desktop experience. Layouts should work well in windows and side-by-side comparisons.',
        },
        {
          title: 'Use consistent iconography',
          description: 'Program identity should be recognizable, but not visually disconnected from MapleOS conventions.',
        },
        {
          title: 'Prefer shadcn/ui patterns',
          description: 'Use the shared component vocabulary for cards, tabs, buttons, inputs, and states so apps feel cohesive.',
        },
        {
          title: 'Keep high-trust actions visible',
          description: 'Important edits, sends, or automations should be explainable and easy to review before execution.',
        },
      ].map((item) => (
        <Card key={item.title} className="border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{item.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

const AutomationDevContent = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-3xl font-bold tracking-tight">Automation & Blueprints</h2>
      <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
        Safe automation in MapleOS should be transparent, permission-aware, and easy to interrupt.
        Blueprints are the preferred mental model: named steps, readable structure, and obvious inputs and outputs.
      </p>
    </div>

    <Card className="border-border/70 bg-muted/30">
      <CardHeader>
        <CardTitle className="text-xl">Safe patterns</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3 text-sm text-muted-foreground sm:text-base">
          <li><span className="font-medium text-foreground">Show the chain.</span> Users should be able to understand the order of operations.</li>
          <li><span className="font-medium text-foreground">Minimize permissions.</span> Ask for the least access needed to finish the workflow well.</li>
          <li><span className="font-medium text-foreground">Keep human checkpoints.</span> High-impact actions should support review or confirmation.</li>
          <li><span className="font-medium text-foreground">Separate grounding from memory.</span> Use the right context tool for the job instead of blending everything together.</li>
        </ul>
      </CardContent>
    </Card>
  </div>
);

const PackagingContent = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-3xl font-bold tracking-tight">Packaging & Maple Market</h2>
      <p className="mt-4 max-w-4xl text-base leading-7 text-muted-foreground">
        Maple Market now supports two install formats for developers: a stable self-contained HTML bundle and a package-based
        <span className="font-medium text-foreground"> .mpak</span> flow for multi-file web apps and packaged WASM modules.
      </p>
      <p className="mt-4 max-w-4xl text-base leading-7 text-muted-foreground">
        HTML bundles are still the simplest option when everything can live in one file. If your app needs multiple files or a packaged WASM entry,
        upload a ZIP renamed to <code className="rounded bg-muted px-1.5 py-0.5 text-xs">.mpak</code> with a root manifest.
      </p>
      <p className="mt-4 max-w-4xl text-base leading-7 text-muted-foreground">
        In both cases, users still get the same MapleOS experience: <span className="font-medium text-foreground">Download → install into VFS → open now</span>.
        No restart should be required.
      </p>
    </div>

    <Alert>
      <Store className="h-4 w-4" />
      <AlertTitle>Current install behavior</AlertTitle>
      <AlertDescription>
        HTML bundles keep their existing hot-load path. <code className="rounded bg-muted px-1.5 py-0.5 text-xs">.mpak</code> installs are unpacked,
        validated, and launched automatically based on the manifest type.
      </AlertDescription>
    </Alert>

    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {[
        {
          title: 'Upload today',
          description: 'Upload one self-contained HTML file for single-file apps, or upload one .mpak package for multi-file web/JS and WASM apps.',
        },
        {
          title: 'WASM today',
          description: 'Inline WASM still works inside a single HTML bundle, and packaged WASM modules now use manifest.entry.wasm inside .mpak uploads.',
        },
        {
          title: 'Required manifest',
          description: 'Every .mpak must include a root manifest.json with name, version, type, loader, and entry.',
        },
        {
          title: 'Install experience',
          description: 'Download writes files into VFS, dispatches a file-system update, and hot-loads the app without restart.',
        },
      ].map((item) => (
        <Card key={item.title} className="border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{item.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>

    <Card className="border-border/70 bg-muted/30">
      <CardHeader>
        <CardTitle className="text-xl">Which file should I upload?</CardTitle>
        <CardDescription>Pick the format that matches how your app is built.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border/70 bg-background/70 p-4">
          <p className="text-sm font-semibold text-foreground">Single-file HTML bundle</p>
          <ul className="mt-3 space-y-2 text-sm leading-6 text-muted-foreground">
            <li>• Upload one <code className="rounded bg-muted px-1.5 py-0.5 text-xs">index.html</code> file.</li>
            <li>• Inline CSS, JS, images, fonts, and icons.</li>
            <li>• If you use WASM here, embed the compiled bytes directly into the HTML.</li>
            <li>• Maple Market generates the install manifest for this format.</li>
          </ul>
        </div>
        <div className="rounded-lg border border-border/70 bg-background/70 p-4">
          <p className="text-sm font-semibold text-foreground"><code className="rounded bg-muted px-1.5 py-0.5 text-xs">.mpak</code> package</p>
          <ul className="mt-3 space-y-2 text-sm leading-6 text-muted-foreground">
            <li>• Zip your app, rename it to <code className="rounded bg-muted px-1.5 py-0.5 text-xs">.mpak</code>, and upload that file.</li>
            <li>• Put <code className="rounded bg-muted px-1.5 py-0.5 text-xs">manifest.json</code> at the package root.</li>
            <li>• Use it for multi-file web/JS apps and packaged WASM modules.</li>
            <li>• MapleOS unpacks the files and launches by manifest type.</li>
          </ul>
        </div>
      </CardContent>
    </Card>

    <div className="grid gap-4 lg:grid-cols-3">
      {[
        {
          title: 'Web HTML bundle',
          status: 'Supported now',
          description: 'Best for self-contained apps that can ship in one HTML file.',
        },
        {
          title: 'HTML bundle with inline WASM',
          status: 'Supported now',
          description: 'Use this when you want a single-file web app but still need WASM.',
        },
        {
          title: '.mpak package',
          status: 'Supported now',
          description: 'Use this for multi-file web/JS apps or packaged WASM modules with a root manifest.',
        },
      ].map((item) => (
        <Card key={item.title} className="border-border/70">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-lg">{item.title}</CardTitle>
              <Badge variant="secondary">{item.status}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">{item.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>

    <Card className="border-border/70">
      <CardHeader>
        <CardTitle className="text-xl">Manifest basics</CardTitle>
        <CardDescription>MapleOS apps are manifest-driven. HTML bundles still get an installer-generated manifest. Every .mpak must include its own root manifest.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <p className="mb-3 text-sm font-medium text-foreground">Installer-generated HTML bundle manifest</p>
          <pre className="overflow-x-auto rounded-lg border border-border/70 bg-muted/60 p-4 text-xs text-muted-foreground"><code>{`{
  "type": "web",
  "loader": "bundle",
  "entry": { "html": "index.html" },
  "assets": "vfs://app/"
}`}</code></pre>
        </div>

        <div>
          <p className="mb-3 text-sm font-medium text-foreground">Required .mpak web manifest</p>
          <pre className="overflow-x-auto rounded-lg border border-border/70 bg-muted/60 p-4 text-xs text-muted-foreground"><code>{`{
  "name": "My Web App",
  "version": "1.0.0",
  "type": "web",
  "loader": "package",
  "entry": { "html": "index.html" }
}`}</code></pre>
        </div>

        <div>
          <p className="mb-3 text-sm font-medium text-foreground">Required .mpak WASM manifest</p>
          <pre className="overflow-x-auto rounded-lg border border-border/70 bg-muted/60 p-4 text-xs text-muted-foreground"><code>{`{
  "name": "My WASM Module",
  "version": "1.0.0",
  "type": "wasm",
  "loader": "package",
  "entry": { "wasm": "build/module.wasm" }
}`}</code></pre>
        </div>
      </CardContent>
    </Card>

    <Card className="border-border/70">
      <CardHeader>
        <CardTitle className="text-xl">What happens when a user clicks Download?</CardTitle>
        <CardDescription>Both delivery formats preserve the no-restart install/open flow.</CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="space-y-3 text-sm leading-7 text-muted-foreground sm:text-base">
          <li><span className="font-medium text-foreground">1. Maple Market resolves a signed URL.</span> MapleOS downloads either HTML text or .mpak archive bytes.</li>
          <li><span className="font-medium text-foreground">2. MapleOS creates a module folder.</span> The install target is <code className="rounded bg-muted px-1.5 py-0.5 text-xs">C:\Program Files\Modules\&lt;App Name&gt;</code>.</li>
          <li><span className="font-medium text-foreground">3. MapleOS writes app files.</span> HTML bundles write <code className="rounded bg-muted px-1.5 py-0.5 text-xs">manifest.json</code> and <code className="rounded bg-muted px-1.5 py-0.5 text-xs">index.html</code>; .mpak installs unpack all files and preserve their manifest.</li>
          <li><span className="font-medium text-foreground">4. The file system announces the change.</span> MapleOS dispatches a file-system update event so the runtime sees the new app immediately.</li>
          <li><span className="font-medium text-foreground">5. The app opens right away.</span> <code className="rounded bg-muted px-1.5 py-0.5 text-xs">type: "web"</code> launches the web runtime and <code className="rounded bg-muted px-1.5 py-0.5 text-xs">type: "wasm"</code> launches ModuleHost.</li>
        </ol>
      </CardContent>
    </Card>

    <Card className="border-border/70 bg-muted/30">
      <CardHeader>
        <CardTitle className="text-xl">How hot-loading works</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm leading-7 text-muted-foreground sm:text-base">
        <p>
          MapleOS behaves like a hot-loading desktop for marketplace installs. The installer writes into the virtual file system,
          notifies the system that files changed, and opens the app immediately.
        </p>
        <p>
          For developers, the expected experience is still: <span className="font-medium text-foreground">Download → install into VFS → open app now</span>.
        </p>
      </CardContent>
    </Card>
  </div>
);

const RoadmapFaqContent = () => (
  <div className="space-y-8">
    <div>
      <h2 className="text-3xl font-bold tracking-tight">Roadmap & FAQ</h2>
      <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground">
        Here are the developer questions we expect most often as Maple Market expands bundle support into full package installs.
      </p>
    </div>

    <div className="space-y-4">
      {[
        {
          question: 'What file should I upload today?',
          answer: 'Upload one self-contained HTML file for a single-file bundle, or upload one .mpak file when your app needs multiple files or a packaged WASM entry.',
        },
        {
          question: 'Can I ship a WASM app right now?',
          answer: 'Yes. You can either inline the WASM inside a single HTML bundle or upload a .mpak package with manifest.entry.wasm.',
        },
        {
          question: 'What does .mpak mean?',
          answer: 'It is just a ZIP archive renamed to .mpak. MapleOS treats it as package content and unpacks it into the module root.',
        },
        {
          question: 'Do installed apps require a MapleOS restart?',
          answer: 'No. The install flow writes into the virtual file system, triggers a file-system update event, and opens the app immediately.',
        },
        {
          question: 'How should developers build MapleOS apps right now?',
          answer: 'Use a self-contained HTML bundle when you can. Use .mpak when you need multi-file web assets or want to ship a packaged WASM module with its own manifest.',
        },
        {
          question: 'What should stay stable as the platform evolves?',
          answer: 'Manifest-driven packaging, automatic runtime routing, permission-aware execution, and the expectation that installs hot-load without restart.',
        },
      ].map((item) => (
        <Card key={item.question} className="border-border/70">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{item.question}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">{item.answer}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

const Docs = ({ embedded = false }: DocsProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<DocsTab>('guides');
  const [activeSection, setActiveSection] = useState(defaultSectionByTab.guides);
  const { search } = useLocation();
  const navigate = useNavigate();
  const routeAppId = new URLSearchParams(search).get('app');
  const [embeddedAppId, setEmbeddedAppId] = useState<string | null>(routeAppId);
  const appId = embedded ? embeddedAppId : routeAppId;
  const selectedProgram = appId ? PROGRAM_MAP[appId] : undefined;

  useEffect(() => {
    if (!embedded) {
      setEmbeddedAppId(routeAppId);
    }
  }, [embedded, routeAppId]);

  const openDocsHome = () => {
    if (embedded) {
      setEmbeddedAppId(null);
      return;
    }

    navigate('/docs');
  };

  const openProgramDocs = (programId: string) => {
    if (embedded) {
      setEmbeddedAppId(programId);
      return;
    }

    navigate(`/docs?app=${programId}`);
  };

  const openProgramInMapleOS = (program: MapleProgram) => {
    if (embedded) {
      window.dispatchEvent(
        new CustomEvent('mapleos-open-app', {
          detail: {
            appId: program.id,
            title: program.label,
          },
        })
      );
      return;
    }

    navigate(`/desktop/start?open=${program.id}`);
  };

  const sortedPrograms = useMemo(
    () => [...PROGRAMS].sort((a, b) => a.label.localeCompare(b.label)),
    []
  );

  const groupedPrograms = useMemo(
    () =>
      sortedPrograms.reduce(
        (acc, program) => {
          if (program.folder) {
            const existingFolder = acc.folders.find(([name]) => name === program.folder);
            if (existingFolder) {
              existingFolder[1].push(program);
            } else {
              acc.folders.push([program.folder, [program]]);
            }
          } else {
            acc.ungrouped.push(program);
          }

          return acc;
        },
        { folders: [] as [string, MapleProgram[]][], ungrouped: [] as MapleProgram[] }
      ),
    [sortedPrograms]
  );

  const visibleSections = activeTab === 'guides' ? userSections : devSections;
  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredSections = visibleSections.filter((section) =>
    section.label.toLowerCase().includes(normalizedSearch)
  );

  const filteredPrograms = useMemo(() => {
    if (!normalizedSearch) {
      return groupedPrograms;
    }

    const matchesProgram = (program: MapleProgram) =>
      [program.label, program.id, program.folder ?? '']
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch);

    return {
      ungrouped: groupedPrograms.ungrouped.filter(matchesProgram),
      folders: groupedPrograms.folders
        .map(([folderName, programs]) => [folderName, programs.filter(matchesProgram)] as [string, MapleProgram[]])
        .filter(([folderName, programs]) => programs.length > 0 || folderNameMatches(folderName, normalizedSearch)),
    };
  }, [groupedPrograms, normalizedSearch]);

  function folderNameMatches(folderName: string, query: string) {
    return folderName.toLowerCase().includes(query);
  }

  useEffect(() => {
    if (!visibleSections.some((section) => section.id === activeSection)) {
      setActiveSection(defaultSectionByTab[activeTab]);
    }
  }, [activeSection, activeTab, visibleSections]);

  useEffect(() => {
    if (normalizedSearch && filteredSections.length > 0 && !filteredSections.some((section) => section.id === activeSection)) {
      setActiveSection(filteredSections[0].id);
    }
  }, [activeSection, filteredSections, normalizedSearch]);

  const renderGuidesContent = () => {
    switch (activeSection) {
      case 'welcome':
        return <WelcomePhilosophyContent />;
      case 'how-it-works':
        return <HowWorksContent />;
      case 'privacy-control':
        return <PrivacyControlContent />;
      case 'apps':
        return <AppsContent />;
      case 'knowledge-memory':
        return <KnowledgeMemoryContent />;
      case 'blueprints':
        return <BlueprintsContent />;
      case 'power-users':
        return <PowerUsersContent />;
      default:
        return <WelcomePhilosophyContent />;
    }
  };

  const renderDeveloperContent = () => {
    switch (activeSection) {
      case 'architecture':
        return <ArchitectureOverviewContent />;
      case 'app-model':
        return <AppModelContent />;
      case 'ui-guidelines':
        return <UIGuidelinesContent />;
      case 'automation':
        return <AutomationDevContent />;
      case 'packaging':
        return <PackagingContent />;
      case 'faq':
        return <RoadmapFaqContent />;
      default:
        return <ArchitectureOverviewContent />;
    }
  };

  const renderContent = () => {
    if (appId) {
      return renderAppDetails();
    }

    return activeTab === 'guides' ? renderGuidesContent() : renderDeveloperContent();
  };

  const renderAppDetails = () => {
    if (appId && !selectedProgram) {
      return (
        <Card className="border-dashed">
          <CardHeader>
            <div className="mb-2">
              {embedded ? (
                <Button variant="ghost" className="-ml-3 h-auto px-3 text-muted-foreground" onClick={openDocsHome}>
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Back to Docs
                </Button>
              ) : (
                <Button asChild variant="ghost" className="-ml-3 h-auto px-3 text-muted-foreground">
                  <Link to="/docs">
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Back to Docs
                  </Link>
                </Button>
              )}
            </div>
            <CardTitle>App documentation not found</CardTitle>
            <CardDescription>
              We couldn&apos;t find a MapleOS app matching <code>{appId}</code>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Return to the documentation overview and choose another app from the MapleOS catalog.
            </p>
          </CardContent>
        </Card>
      );
    }

    if (!selectedProgram) {
      return activeTab === 'guides' ? renderGuidesContent() : renderDeveloperContent();
    }

    const appDoc = getAppDocContent(selectedProgram);
    const ProgramIcon = selectedProgram.icon;

    return (
      <div className="space-y-8">
        <div className="space-y-4">
          {embedded ? (
            <Button variant="ghost" className="-ml-3 h-auto px-3 text-muted-foreground" onClick={openDocsHome}>
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back to Docs
            </Button>
          ) : (
            <Button asChild variant="ghost" className="-ml-3 h-auto px-3 text-muted-foreground">
              <Link to="/docs">
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back to Docs
              </Link>
            </Button>
          )}

          <Card className="overflow-hidden border-border/70">
            <CardHeader className="gap-5 bg-muted/30 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border bg-background shadow-sm">
                  <ProgramIcon size={48} className="h-12 w-12" />
                </div>
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <CardTitle className="text-3xl tracking-tight">{selectedProgram.label}</CardTitle>
                    <Badge variant="secondary">{selectedProgram.folder ?? 'Core'}</Badge>
                  </div>
                  <CardDescription className="max-w-2xl text-sm leading-6">
                    {selectedProgram.description ?? `${selectedProgram.label} is part of the MapleOS app ecosystem.`}
                  </CardDescription>
                </div>
              </div>
              {embedded ? (
                <Button type="button" className="w-full sm:w-auto" onClick={() => openProgramInMapleOS(selectedProgram)}>
                  Open in MapleOS
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button asChild className="w-full sm:w-auto">
                  <Link to={`/desktop/start?open=${selectedProgram.id}`} aria-label={`Open ${selectedProgram.label} in MapleOS`}>
                    Open in MapleOS
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-6 lg:p-8">
              <div className="grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:items-start">
                <div className="overflow-hidden rounded-2xl border bg-muted/40 shadow-sm">
                  <img
                    src={appDoc.image}
                    alt={`${selectedProgram.label} screenshot`}
                    className="h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = FALLBACK_APP_IMAGE;
                    }}
                  />
                </div>

                <div className="space-y-6">
                  <div className="space-y-4 text-sm leading-7 text-muted-foreground sm:text-base">
                    {appDoc.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>

                  <div className="space-y-3">
                    <h2 className="text-lg font-semibold text-foreground">Key features</h2>
                    <ul className="space-y-3">
                      {appDoc.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3 rounded-xl border bg-card px-4 py-3 text-sm text-muted-foreground">
                          <span className="mt-1 h-2 w-2 rounded-full bg-primary" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const seoTitle = selectedProgram ? `${selectedProgram.label} Documentation` : 'MapleOS Docs';
  const seoDescription = selectedProgram?.description ?? 'Explore MapleOS — a desktop OS with apps, privacy, and human-first control.';
  const programsCatalogLabel = activeTab === 'guides' ? 'Apps' : 'Programs Catalog';
  const showProgramCatalog = activeTab === 'guides' || Boolean(searchQuery) || Boolean(appId);

  const docsLayout = (
    <div className={embedded ? 'h-full overflow-auto bg-background px-4 py-5 sm:px-6' : 'container mx-auto px-4 py-8 sm:px-6'}>
      <div className={embedded ? 'grid h-full min-h-0 grid-cols-1 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]' : 'grid grid-cols-1 gap-8 lg:grid-cols-[280px_minmax(0,1fr)]'}>
        <aside className={embedded ? 'lg:self-start lg:border-r lg:pr-6' : 'lg:sticky lg:top-24 lg:self-start lg:border-r lg:pr-6'} aria-label="Documentation navigation">
          <div className="mb-6">
            <h2 className="mb-2 text-lg font-semibold">Documentation</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search docs and apps..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="pl-10"
                aria-label="Search documentation"
              />
            </div>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              const nextTab = value as DocsTab;
              setActiveTab(nextTab);
              setActiveSection(defaultSectionByTab[nextTab]);
            }}
            className="mb-6"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="guides">User Guides</TabsTrigger>
              <TabsTrigger value="dev">Developer Docs</TabsTrigger>
            </TabsList>
          </Tabs>

          <ScrollArea className={embedded ? 'h-[calc(100vh-18rem)] lg:h-[calc(100vh-13rem)] pr-4' : 'max-h-[calc(100dvh-14rem)] pr-4'}>
            <div className="space-y-1">
              {filteredSections.length > 0 ? (
                filteredSections.map((section) => (
                  <Button
                    key={section.id}
                    variant={!appId && activeSection === section.id ? 'secondary' : 'ghost'}
                    className="w-full justify-start"
                    onClick={() => {
                      setActiveSection(section.id);
                      if (appId) {
                        openDocsHome();
                      }
                    }}
                  >
                    {section.icon}
                    <span className="ml-2 truncate">{section.label}</span>
                  </Button>
                ))
              ) : (
                <p className="px-3 py-2 text-sm text-muted-foreground">No sections match your search.</p>
              )}
            </div>

            <Separator className="my-4" />

            <div className="space-y-2">
              <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {programsCatalogLabel}
              </div>
              {activeTab === 'dev' && (
                <p className="px-3 text-xs leading-5 text-muted-foreground">
                  Reference IDs for deep links and program lookup.
                </p>
              )}

              {showProgramCatalog ? (
                <div className="space-y-1">
                  {filteredPrograms.ungrouped.map((program) => {
                    const ProgramItemIcon = program.icon;

                    return (
                      <Button
                        key={program.id}
                        variant={appId === program.id ? 'secondary' : 'ghost'}
                        className="w-full justify-start gap-2.5 text-sm"
                        onClick={() => openProgramDocs(program.id)}
                      >
                        <ProgramItemIcon size={20} className="h-5 w-5 shrink-0" />
                        <span className="truncate">{program.label}</span>
                      </Button>
                    );
                  })}

                  {filteredPrograms.folders.map(([folderName, programs]) => (
                    <Collapsible
                      key={`${folderName}-${appId ?? 'root'}`}
                      defaultOpen={activeTab === 'guides' ? programs.some((program) => program.id === appId) : Boolean(searchQuery || appId)}
                    >
                      <CollapsibleTrigger
                        showIcon={true}
                        className="rounded-md px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                      >
                        <span className="flex min-w-0 items-center gap-2.5">
                          <DocsFolderIcon className="h-5 w-5 shrink-0" />
                          <span className="truncate">{folderName}</span>
                        </span>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-1 pl-5 pt-1">
                        {programs.map((program) => {
                          const ProgramItemIcon = program.icon;

                          return (
                            <Button
                              key={program.id}
                              variant={appId === program.id ? 'secondary' : 'ghost'}
                              className="w-full justify-start gap-2.5 text-sm"
                              onClick={() => openProgramDocs(program.id)}
                            >
                              <ProgramItemIcon size={20} className="h-5 w-5 shrink-0" />
                              <span className="truncate">{program.label}</span>
                            </Button>
                          );
                        })}
                      </CollapsibleContent>
                    </Collapsible>
                  ))}

                  {filteredPrograms.ungrouped.length === 0 && filteredPrograms.folders.length === 0 && (
                    <p className="px-3 py-2 text-sm text-muted-foreground">No apps match your search.</p>
                  )}
                </div>
              ) : (
                <p className="px-3 py-2 text-sm text-muted-foreground">Switch to app search or open a program from User Guides to browse the catalog.</p>
              )}
            </div>
          </ScrollArea>
        </aside>

        <main className={embedded ? 'min-w-0 pb-6 lg:pt-2' : 'min-w-0 pb-16 lg:pt-2'} aria-live="polite">
          {renderContent()}
        </main>
      </div>
    </div>
  );

  if (embedded) {
    return docsLayout;
  }

  return (
    <LandingShell>
      <SEO title={seoTitle} description={seoDescription} />
      {docsLayout}
    </LandingShell>
  );
};

export default Docs;